import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Plus, BarChart3, List, CheckSquare, ArrowLeft, FileText } from 'lucide-react';
import { useJobs } from '@/hooks/useJobs';
import { useMilestones, useCreateMilestone, useMilestoneApproval, useUpdateMilestone, useRequestMilestoneChange, useMarkMilestoneComplete, usePendingMilestoneChanges } from '@/hooks/useMilestones';
import { useAuth } from '@/lib/auth';
import { Link } from 'react-router-dom';
import { MilestoneForm } from '@/components/milestones/MilestoneForm';
import { MilestoneList } from '@/components/milestones/MilestoneList';
import { MilestoneTimeline } from '@/components/milestones/MilestoneTimeline';
import { MilestoneApprovalComponent } from '@/components/milestones/MilestoneApproval';
import { MilestoneCompletionDialog } from '@/components/milestones/MilestoneCompletionDialog';
import { MilestoneSetSelector } from '@/components/milestones/MilestoneSetSelector';
import { MilestoneApprovalCompleteModal } from '@/components/milestones/MilestoneApprovalCompleteModal';
import { PurchaseAgreementModal } from '@/components/milestones/PurchaseAgreementModal';
import { PaymentProcessingModal } from '@/components/milestones/PaymentProcessingModal';
import { MilestoneApprovalBanner } from '@/components/milestones/MilestoneApprovalBanner';
import { AnchorMilestonesNotice } from '@/components/milestones/AnchorMilestonesNotice';
import { MilestonesEducationalNotice } from '@/components/milestones/MilestonesEducationalNotice';
import type { CreateMilestoneData, UpdateMilestoneData, Milestone } from '@/lib/types/milestone';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useCreatePurchaseOrder } from '@/hooks/usePurchaseOrders';

import { PaymentMethodSelectionModal } from '@/components/payments/PaymentMethodSelectionModal';
import { useCustomerPaymentMethods } from '@/hooks/useCustomerPaymentMethods';
import { usePlatformSetting } from '@/hooks/usePlatformSettings';
import { AchPendingBanner } from '@/components/payments/AchPendingBanner';
import { calculateCcProcessingFee } from '@/lib/paymentUtils';
import { format } from 'date-fns';
import { useTaxPreview } from '@/hooks/useTaxPreview';

export default function JobMilestonesPage() {
  const params = useParams();
  const jobId = params.jobId as string;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState<any>(null);
  const [requestingChangeMilestone, setRequestingChangeMilestone] = useState<any>(null);
  const [changeReason, setChangeReason] = useState('');
  const [showEditForm, setShowEditForm] = useState(false);
  const [showChangeRequestForm, setShowChangeRequestForm] = useState(false);
  const [completingMilestone, setCompletingMilestone] = useState<Milestone | null>(null);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showMilestoneSetSelector, setShowMilestoneSetSelector] = useState(false);
  const [showApprovalCompleteModal, setShowApprovalCompleteModal] = useState(false);
  const [showPurchaseAgreementModal, setShowPurchaseAgreementModal] = useState(false);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showPaymentProcessingModal, setShowPaymentProcessingModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [paymentError, setPaymentError] = useState<string>('');
  const [paymentErrorDetails, setPaymentErrorDetails] = useState<{
    recommendation?: string;
    code?: string;
    canRetry?: boolean;
    suggestAch?: boolean;
    suggestUpdatePayment?: boolean;
    severity?: 'temporary' | 'permanent' | 'requires_action';
  }>();
  const [paymentTransactionId, setPaymentTransactionId] = useState<string>('');
  const [hasPurchaseOrder, setHasPurchaseOrder] = useState(false);
  const [winningBidId, setWinningBidId] = useState<string | undefined>();
  const [currentPaymentMethodId, setCurrentPaymentMethodId] = useState<string | undefined>();
  const [organizationId, setOrganizationId] = useState<string>('');
  const { toast } = useToast();
  const { createPurchaseOrder, loading: creatingPO } = useCreatePurchaseOrder();
  const [anchorsEnsured, setAnchorsEnsured] = useState(false);
  const [achPending, setAchPending] = useState(false);
  const [achSettlementDate, setAchSettlementDate] = useState<string>('');

  // Get payment methods for CC fee calculation (only for clients)
  const isClientUser = user?.role === 'client';
  const { paymentMethods } = useCustomerPaymentMethods(isClientUser ? organizationId : '');
  const { data: ccFeeSetting } = usePlatformSetting('cc_processing_fee_percentage');
  const ccFeePercentage = ccFeeSetting ? parseFloat(ccFeeSetting.setting_value) : 2.9;

  const { jobs, loading: jobLoading } = useJobs({});
  const job = jobs.find(j => j.public_id === jobId || j.id === jobId);

  // Get tax preview for the job (must be after job is defined)
  const { data: taxPreview } = useTaxPreview(job?.id);

  const { milestones, loading: milestonesLoading, refetch: refetchMilestones } = useMilestones(job?.id);
  const { approval, loading: approvalLoading, refetch: refetchApproval } = useMilestoneApproval(job?.id);
  const { pendingChanges, loading: pendingChangesLoading } = usePendingMilestoneChanges(job?.id || '');
  const { createMilestone, loading: createLoading } = useCreateMilestone();
  const { updateMilestone, loading: updateLoading } = useUpdateMilestone();
  const { requestChange, loading: changeRequestLoading } = useRequestMilestoneChange();
  const { markComplete, loading: markCompleteLoading } = useMarkMilestoneComplete();

  // Determine user role based on job and user relationship
  const getUserRole = (): 'client' | 'vendor' => {
    if (!job || !user) return 'client';
    
    // Check if user is from the job's organization (client)
    if (user.organization_id === job.organization_id) {
      return 'client';
    }
    
    return 'vendor';
  };

  const userRole = getUserRole();
  const canCreateMilestones = userRole === 'client' || (userRole === 'vendor' && job?.status === 'awarded');
  
  // Function to check if user can edit a specific milestone
  const canEditMilestone = (milestone: any) => {
    if (isFullyApproved || milestone.is_locked) return false;
    
    // If milestone is blank, only the action_owner can edit it
    if (milestone.is_blank) {
      return milestone.action_owner === userRole;
    }
    
    // Otherwise, use general edit permissions
    return canCreateMilestones;
  };

  const handleCreateMilestone = async (data: CreateMilestoneData) => {
    try {
      await createMilestone(job?.id || jobId, data);
      setShowCreateForm(false);
      refetchMilestones();
    } catch (error) {
      console.error('Failed to create milestone:', error);
    }
  };

  const handleRequestChange = (milestone: any) => {
    setRequestingChangeMilestone(milestone);
    setChangeReason('');
    setShowChangeRequestForm(true);
  };

  const handleEditMilestone = (milestone: any) => {
    setEditingMilestone(milestone);
    setShowEditForm(true);
  };

  const handleUpdateMilestone = async (data: UpdateMilestoneData) => {
    if (!editingMilestone) return;
    
    try {
      await updateMilestone(editingMilestone.id, {
        ...data,
        is_blank: false // Mark as no longer blank when completing
      });
      setShowEditForm(false);
      setEditingMilestone(null);
      refetchMilestones();
    } catch (error) {
      console.error('Failed to update milestone:', error);
    }
  };

  const handleRequestMilestoneChange = async (data: any) => {
    if (!requestingChangeMilestone) return;

    try {
      // Compare form data with original milestone to find changed fields
      const changedFields: Record<string, any> = {};
      
      // List of fields to check
      const fieldsToCheck = [
        'milestone_name',
        'due_date',
        'description',
        'milestone_type',
        'deliverable',
        'action_owner',
        'milestone_order'
      ];
      
      // Only include fields that have actually changed
      for (const field of fieldsToCheck) {
        const oldValue = requestingChangeMilestone[field as keyof typeof requestingChangeMilestone];
        const newValue = data[field];
        
        // Compare values - handle null/undefined/empty string equivalence
        const normalizedOld = oldValue ?? '';
        const normalizedNew = newValue ?? '';
        
        if (normalizedOld !== normalizedNew) {
          changedFields[field] = newValue;
        }
      }
      
      // If no fields changed, don't allow the change request
      if (Object.keys(changedFields).length === 0) {
        toast({
          title: 'No Changes',
          description: 'No fields were modified. Please make changes before requesting approval.',
          variant: 'destructive',
        });
        return;
      }
      
      await requestChange(
        requestingChangeMilestone.id, 
        { ...changedFields, change_reason: changeReason },
        getUserRole(),
        () => {
          refetchMilestones();
          refetchApproval();
        }
      );
      setShowChangeRequestForm(false);
      setRequestingChangeMilestone(null);
      setChangeReason('');
    } catch (error) {
      console.error('Failed to request change:', error);
    }
  };

  const handleApprove = async () => {
    try {
      const { data: milestoneApprovalData } = await supabase
        .from('milestone_approvals')
        .select('*')
        .eq('job_id', job?.id || jobId)
        .single();

      // Determine the updated status
      let newStatus = '';
      if (userRole === 'client') {
        if (milestoneApprovalData?.vendor_approved_at) {
          newStatus = 'approved_by_both';
        } else {
          newStatus = 'approved_by_client';
        }
      } else {
        if (milestoneApprovalData?.client_approved_at) {
          newStatus = 'approved_by_both';
        } else {
          newStatus = 'approved_by_vendor';
        }
      }

      // Update the approval
      const updateData: any = {
        approval_status: newStatus,
      };
      if (userRole === 'client') {
        updateData.client_approved_at = new Date().toISOString();
      } else {
        updateData.vendor_approved_at = new Date().toISOString();
      }

      await supabase
        .from('milestone_approvals')
        .update(updateData)
        .eq('job_id', job?.id || jobId);

      // Notify other party
      await invokeEdgeFunction('milestone-notify', {
        body: {
          type: 'approve',
          jobId: job?.id || jobId,
          approvalType: userRole,
        },
        callerOperation: 'MilestonesPage.handleApprove',
      });
      
      await handleApprovalChange();
      
      toast({
        title: '✓ Milestones Approved',
        description: 'Your approval has been recorded. The other party will be notified.',
      });
    } catch (error) {
      console.error('Failed to approve milestones:', error);
      toast({
        title: 'Error',
        description: 'Failed to approve milestones',
        variant: 'destructive',
      });
    }
  };

  const handleApprovalChange = async () => {
    await refetchApproval();
    refetchMilestones();
    
    // Check if both parties just approved
    const { data: currentApproval } = await supabase
      .from('milestone_approvals')
      .select('*')
      .eq('job_id', job?.id || jobId)
      .single();
    
    if (currentApproval?.approval_status === 'approved_by_both' && userRole === 'client' && !hasPurchaseOrder) {
      setShowApprovalCompleteModal(true);
    }
  };

  // Check for existing PO and winning bid, fetch payment setup info
  const checkPOAndBid = async () => {
    if (!job?.id) return;
    
    const { data: po } = await supabase
      .from('purchase_orders')
      .select('id')
      .eq('job_id', job.id)
      .maybeSingle();
    
    setHasPurchaseOrder(!!po);

    // Get winning bid ID
    const { data: award } = await supabase
      .from('awards')
      .select('winning_bid_id')
      .eq('job_id', job.id)
      .maybeSingle();
    
    if (award?.winning_bid_id) {
      setWinningBidId(award.winning_bid_id);
    }

    // Get payment setup info
    const { data: paymentSetup } = await supabase
      .from('payment_setup_status')
      .select('stripe_payment_method_id')
      .eq('job_id', job.id)
      .maybeSingle();
    
    if (paymentSetup?.stripe_payment_method_id) {
      setCurrentPaymentMethodId(paymentSetup.stripe_payment_method_id);
    }

    // Set organization ID
    if (job.organization_id) {
      setOrganizationId(job.organization_id);
    }

    // Check for existing ACH pending transactions
    const { data: achTransaction } = await supabase
      .from('payment_transactions')
      .select('status, ach_settlement_expected_at')
      .eq('job_id', job.id)
      .eq('payment_method_type', 'traditional_ach')
      .eq('status', 'ach_pending')
      .is('ach_settled_at', null)
      .maybeSingle();

    if (achTransaction?.ach_settlement_expected_at) {
      setAchPending(true);
      setAchSettlementDate(achTransaction.ach_settlement_expected_at);
    }
  };

  useEffect(() => {
    checkPOAndBid();
  }, [job?.id, job?.organization_id]);

  // Handle query parameter actions
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add') {
      setShowCreateForm(true);
      setSearchParams({}, { replace: true });
    } else if (action === 'template') {
      setShowMilestoneSetSelector(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Detect return from Stripe payment method setup and auto-open modal
  useEffect(() => {
    const paymentMethodAdded = searchParams.get('payment_method_added');
    const paymentMethodCancelled = searchParams.get('payment_method_cancelled');
    
    if (paymentMethodAdded === 'true' || paymentMethodCancelled === 'true') {
      // Auto-open the payment method modal (skip purchase agreement - already accepted before redirect)
      setShowPaymentMethodModal(true);
      
      // Calculate payment amount if needed (in case state was lost on redirect)
      if (winningBidId && paymentAmount === 0) {
        const calculateAmount = async () => {
          try {
            const { data: award } = await supabase
              .from('awards')
              .select('id')
              .eq('winning_bid_id', winningBidId)
              .single();
            
            if (award) {
              const { data: selections } = await supabase
                .from('award_selections')
                .select('total_price_cents')
                .eq('award_id', award.id);
              
              const total = selections?.reduce((sum, s) => sum + s.total_price_cents, 0) || 0;
              setPaymentAmount(total);
            }
          } catch (error) {
            console.error('Error calculating payment amount:', error);
          }
        };
        calculateAmount();
      }
      
      // Clean up URL params
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('payment_method_added');
        newParams.delete('payment_method_cancelled');
        return newParams;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams, winningBidId, paymentAmount]);

  // Real-time subscription for ACH settlement
  useEffect(() => {
    if (!job?.id || !achPending) return;

    const channel = supabase
      .channel(`ach-settlement-${job.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_transactions',
          filter: `job_id=eq.${job.id}`,
        },
        (payload) => {
          const newData = payload.new as any;
          // If ACH has settled, clear the pending state
          if (newData.ach_settled_at && achPending) {
            setAchPending(false);
            setAchSettlementDate('');
            toast({
              title: 'Payment Settled!',
              description: 'Your ACH payment has cleared and your Purchase Order has been created.',
            });
            // Refresh data
            checkPOAndBid();
            refetchMilestones();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [job?.id, achPending]);

  // Ensure anchor milestones exist (idempotent - runs once per visit)
  useEffect(() => {
    if (!job?.id || milestonesLoading || anchorsEnsured) return;
    // Check if user-created milestones exist (exclude system milestones)
    const userCreatedMilestones = milestones.filter(m => !(m as any).is_system_milestone);
    if (userCreatedMilestones.length > 0) {
      setAnchorsEnsured(true);
      return;
    }

    (async () => {
      try {
        // Check if payment setup is complete by both parties
        const { data: paymentSetupStatus } = await supabase
          .from('payment_setup_status')
          .select('both_complete')
          .eq('job_id', job.id)
          .maybeSingle();

        // Only create anchor milestones if payment setup is complete
        if (!paymentSetupStatus?.both_complete) {
          setAnchorsEnsured(true);
          return;
        }

        await invokeEdgeFunction('create-anchor-milestones', {
          body: { jobId: job.id },
          callerOperation: 'MilestonesPage.ensureAnchorMilestones',
        });
        await refetchMilestones();
      } catch (e) {
        console.error('Failed to ensure anchor milestones:', e);
      } finally {
        setAnchorsEnsured(true);
      }
    })();
  }, [job?.id, milestonesLoading, milestones.length, anchorsEnsured]);

  const handleMarkComplete = (milestone: Milestone) => {
    setCompletingMilestone(milestone);
    setShowCompletionDialog(true);
  };

  const handleConfirmCompletion = async () => {
    if (!completingMilestone) return;

    try {
      await markComplete(completingMilestone.id);
      setShowCompletionDialog(false);
      setCompletingMilestone(null);
      refetchMilestones();
    } catch (error) {
      console.error('Failed to mark milestone complete:', error);
    }
  };

  const handleApplyMilestoneSet = async (milestoneSet: any) => {
    try {
      const baseOrder = Math.max(0, ...milestones.map(m => m.milestone_order)) + 1;
      
      for (let i = 0; i < milestoneSet.milestone_template_items.length; i++) {
        const item = milestoneSet.milestone_template_items[i];
        const milestoneData: any = {
          milestone_name: item.milestone_name,
          description: item.description,
          milestone_type: item.milestone_type,
          deliverable: item.deliverable,
          action_owner: item.action_owner,
          milestone_order: baseOrder + i,
          is_blank: item.is_blank || false
        };
        
        // Only include due_date if it has a valid value
        if (item.due_date) {
          milestoneData.due_date = item.due_date;
        }
        
        await createMilestone(job?.id || jobId, milestoneData);
      }
      
      toast({
        title: 'Milestone set applied',
        description: `${milestoneSet.milestone_template_items.length} milestones added successfully`,
      });
      
      refetchMilestones();
    } catch (error) {
      console.error('Failed to apply milestone set:', error);
      toast({
        title: 'Error',
        description: 'Failed to apply milestone set',
        variant: 'destructive',
      });
    }
  };

  if (jobLoading) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Loading job details...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="container mx-auto py-8">
        <div className="text-center">Job not found</div>
      </div>
    );
  }

  const nextOrder = Math.max(0, ...milestones.map(m => m.milestone_order)) + 1;
  const hasBlankMilestones = milestones.some(m => m.is_blank);
  const isFullyApproved = approval?.approval_status === 'approved_by_both';
  const userCreatedMilestones = milestones.filter(m => !(m as any).is_system_milestone);

  const handleShowPurchaseAgreement = async () => {
    setShowApprovalCompleteModal(false);
    
    // Calculate payment amount before showing modal
    if (winningBidId) {
      try {
        const { data: award } = await supabase
          .from('awards')
          .select('id')
          .eq('winning_bid_id', winningBidId)
          .single();
        
        if (award) {
          const { data: selections } = await supabase
            .from('award_selections')
            .select('total_price_cents')
            .eq('award_id', award.id);
          
          const total = selections?.reduce((sum, s) => sum + s.total_price_cents, 0) || 0;
          setPaymentAmount(total);
        }
      } catch (error) {
        console.error('Error calculating payment amount:', error);
      }
    }
    
    setShowPurchaseAgreementModal(true);
  };

  const handleAcceptAgreement = () => {
    // Close agreement modal and show payment method selection
    setShowPurchaseAgreementModal(false);
    setShowPaymentMethodModal(true);
  };

  const handlePaymentMethodConfirmed = async (selectedPaymentMethodId: string) => {
    if (!job?.id || !winningBidId) {
      toast({
        title: 'Error',
        description: 'Missing job or bid information',
        variant: 'destructive'
      });
      return;
    }

    // Determine if selected method is a card for fee calculation
    const selectedMethod = paymentMethods?.find(
      pm => pm.stripe_payment_method_id === selectedPaymentMethodId
    );
    const isCardPayment = selectedMethod?.payment_method_type === 'card';

    try {
      console.log('[PO Flow] Starting purchase order creation with payment method:', selectedPaymentMethodId);
      
      // Close payment method modal
      setShowPaymentMethodModal(false);
      
      await createPurchaseOrder(
        job.id, 
        winningBidId, 
        true,
        {
          selectedPaymentMethodId,
          onPaymentStart: (baseAmount: number) => {
            // Calculate total with CC fee if card payment using pass-through formula
            const ccProcessingFeeCents = isCardPayment 
              ? calculateCcProcessingFee(baseAmount, ccFeePercentage) 
              : 0;
            const totalAmount = baseAmount + ccProcessingFeeCents;
            
            setPaymentAmount(totalAmount);
            setPaymentStatus('processing');
            setPaymentError('');
            setPaymentTransactionId('');
            setShowPaymentProcessingModal(true);
          },
          onPaymentSuccess: (transactionId: string) => {
            setPaymentStatus('success');
            setPaymentTransactionId(transactionId);
            
            // Auto-close after 2 seconds and navigate
            setTimeout(async () => {
              setShowPaymentProcessingModal(false);
              
              const { data: po } = await supabase
                .from('purchase_orders')
                .select('public_id')
                .eq('job_id', job.id)
                .maybeSingle();
              
              if (po?.public_id) {
                navigate(`/purchase-orders/${po.public_id}`);
              } else {
                navigate(`/jobs/${job.public_id}`);
              }
            }, 2000);
          },
          onPaymentError: (error: string, details?: {
            recommendation?: string;
            code?: string;
            canRetry?: boolean;
            suggestAch?: boolean;
            suggestUpdatePayment?: boolean;
            severity?: 'temporary' | 'permanent' | 'requires_action';
          }) => {
            setPaymentStatus('error');
            setPaymentError(error);
            setPaymentErrorDetails(details);
          },
          onAchPending: (settlementEta: string) => {
            // Close processing modal
            setShowPaymentProcessingModal(false);
            
            // Store ACH pending state
            setAchPending(true);
            setAchSettlementDate(settlementEta);
            
            let settlementDateText = '3-5 business days';
            if (settlementEta) {
              try {
                settlementDateText = format(new Date(settlementEta), 'MMM d, yyyy');
              } catch (error) {
                console.error('Invalid settlement date:', settlementEta, error);
              }
            }
            
            toast({
              title: 'ACH Payment Initiated',
              description: `Your bank transfer is being processed. The Purchase Order will be automatically created when payment settles in ${settlementDateText}.`,
              duration: 7000,
            });
          },
          onInvoicePending: (invoice: { invoiceId: string; invoiceNumber: string; invoiceUrl: string; pdfUrl?: string }) => {
            // Close processing modal
            setShowPaymentProcessingModal(false);
            
            toast({
              title: 'Invoice Sent',
              description: `Invoice #${invoice.invoiceNumber || 'pending'} has been sent. The Purchase Order will be created automatically once payment is received.`,
              duration: 7000,
            });
            
            // Refresh page after brief delay to show updated state
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          }
        }
      );
    } catch (error) {
      console.error('Failed to create PO:', error);
      if (!showPaymentProcessingModal) {
        toast({
          title: 'Error',
          description: 'Failed to process payment. Please try again.',
          variant: 'destructive'
        });
      }
    }
  };

  const handleIssuePOClick = async () => {
    // Calculate payment amount before showing modal
    if (winningBidId) {
      try {
        const { data: award } = await supabase
          .from('awards')
          .select('id')
          .eq('winning_bid_id', winningBidId)
          .single();
        
        if (award) {
          const { data: selections } = await supabase
            .from('award_selections')
            .select('total_price_cents')
            .eq('award_id', award.id);
          
          const total = selections?.reduce((sum, s) => sum + s.total_price_cents, 0) || 0;
          setPaymentAmount(total);
        }
      } catch (error) {
        console.error('Error calculating payment amount:', error);
      }
    }
    setShowPurchaseAgreementModal(true);
  };

  const handleReviewDetails = () => {
    setShowPurchaseAgreementModal(false);
    navigate(`/jobs/${job.public_id}`);
  };

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Breadcrumb Navigation */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link to="/jobs" className="hover:text-foreground">Jobs</Link>
        <span>/</span>
        <Link to={`/jobs/${job.public_id}`} className="hover:text-foreground">{job.title}</Link>
        <span>/</span>
        <span className="text-foreground">Milestones</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Link 
              to={`/jobs/${job.public_id}`} 
              className="inline-flex items-center text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Job
            </Link>
          </div>
          <h1 className="text-3xl font-bold">Project Milestones</h1>
          <p className="text-muted-foreground">
            Collaborate on milestone planning for: {job.title}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {approval && (
            <Badge 
              variant={isFullyApproved ? 'default' : 'secondary'}
              className={isFullyApproved ? 'bg-green-100 text-green-800' : ''}
            >
              {approval.approval_status.replace(/_/g, ' ')}
            </Badge>
          )}
          
          {/* Approve Milestones Button - show when user hasn't approved yet and milestones can be approved */}
          {!isFullyApproved && approval && !hasBlankMilestones && userCreatedMilestones.length > 0 && (
            (userRole === 'client' && !approval.client_approved_at) ||
            (userRole === 'vendor' && !approval.vendor_approved_at)
          ) && (
            <Button 
              onClick={handleApprove}
              variant="default"
              className="bg-green-600 hover:bg-green-700"
            >
              <CheckSquare className="h-4 w-4 mr-2" />
              Approve Milestones
            </Button>
          )}
          
          {canCreateMilestones && !isFullyApproved && (
            <>
              {milestones.length === 0 && (
                <Button 
                  variant="outline"
                  onClick={() => setShowMilestoneSetSelector(true)}
                >
                  Apply Milestone Set
                </Button>
              )}
              <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Milestone
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Create New Milestone</DialogTitle>
                  </DialogHeader>
                  <MilestoneForm
                    onSubmit={handleCreateMilestone}
                    loading={createLoading}
                    nextOrder={nextOrder}
                  />
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Edit Milestone Dialog */}
      <Dialog open={showEditForm} onOpenChange={setShowEditForm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingMilestone?.is_blank ? 'Complete Milestone' : 'Edit Milestone'}
            </DialogTitle>
          </DialogHeader>
          {editingMilestone && (
            <MilestoneForm
              onSubmit={handleUpdateMilestone}
              loading={updateLoading}
              initialData={{
                milestone_name: editingMilestone.milestone_name,
                due_date: editingMilestone.due_date,
                description: editingMilestone.description,
                milestone_type: editingMilestone.milestone_type,
                deliverable: editingMilestone.deliverable,
                action_owner: editingMilestone.action_owner,
                milestone_order: editingMilestone.milestone_order,
                is_blank: false
              }}
              nextOrder={editingMilestone.milestone_order}
            />
          )}
        </DialogContent>
        </Dialog>

        {/* Request Change Dialog */}
        <Dialog open={showChangeRequestForm} onOpenChange={setShowChangeRequestForm}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Request Milestone Change</DialogTitle>
            </DialogHeader>
            {requestingChangeMilestone && (
              <MilestoneForm
                onSubmit={handleRequestMilestoneChange}
                loading={changeRequestLoading}
                mode="requestChange"
                changeReason={changeReason}
                onChangeReasonUpdate={setChangeReason}
                initialData={{
                  milestone_name: requestingChangeMilestone.milestone_name,
                  due_date: requestingChangeMilestone.due_date,
                  description: requestingChangeMilestone.description,
                  milestone_type: requestingChangeMilestone.milestone_type,
                  deliverable: requestingChangeMilestone.deliverable,
                  action_owner: requestingChangeMilestone.action_owner,
                  milestone_order: requestingChangeMilestone.milestone_order,
                  is_blank: false
                }}
                nextOrder={requestingChangeMilestone.milestone_order}
              />
            )}
          </DialogContent>
        </Dialog>

      {/* Anchor Milestones Notice - Dismissible */}
      {milestones.some((m: any) => m.is_anchor_milestone) && job?.id && (
        <AnchorMilestonesNotice jobId={job.id} />
      )}

      {/* Educational Notice */}
      <MilestonesEducationalNotice jobId={jobId} />

      {/* Blank Milestones Warning */}
      {hasBlankMilestones && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <CheckSquare className="h-5 w-5 text-orange-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-orange-800">Action Required</h3>
                <p className="text-orange-700 mt-1">
                  There are {milestones.filter(m => m.is_blank).length} blank milestone(s) that need to be completed 
                  before the timeline can be approved by both parties.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Milestone Approved - PO Required Banner */}
      {isFullyApproved && !hasPurchaseOrder && !achPending && userRole === 'client' && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-800">Next Step: Issue Purchase Order</h3>
                  <p className="text-green-700 mt-1">
                    Milestones have been approved by both parties. Issue the purchase order to move the job to in-progress status.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleIssuePOClick} 
                size="sm"
                disabled={creatingPO}
              >
                Issue Purchase Order
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ACH Pending Banner */}
      {achPending && achSettlementDate && (
        <AchPendingBanner 
          settlementDate={achSettlementDate}
          showDetailedMessage={true}
        />
      )}

      {/* Main Content */}
      <div className="space-y-6">
        {/* Timeline View */}
        {milestonesLoading ? (
          <Card>
            <CardContent className="text-center py-8">
              <p>Loading milestones...</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <MilestoneTimeline 
              milestones={milestones} 
              onMarkComplete={handleMarkComplete}
              onRequestChange={handleRequestChange}
              onEditMilestone={handleEditMilestone}
              currentUserRole={userRole}
              canEdit={!isFullyApproved}
              canRequestChange={true}
              isFullyApproved={isFullyApproved}
              jobPublicId={job?.public_id}
              currentUserId={user?.id}
            />
            
            {/* Add Milestone Button at Bottom */}
            {canCreateMilestones && !isFullyApproved && (
              <div className="flex justify-center pt-4">
                <Button 
                  onClick={() => setShowCreateForm(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Milestone
                </Button>
              </div>
            )}
          </>
        )}

        {/* Milestone Approval Section - Show when not fully approved OR when there are pending changes */}
        {approvalLoading || pendingChangesLoading ? (
          <Card>
            <CardContent className="text-center py-8">
              <p>Loading approval status...</p>
            </CardContent>
          </Card>
        ) : (!isFullyApproved || (pendingChanges && pendingChanges.length > 0)) ? (
          <MilestoneApprovalComponent
            approval={approval}
            milestones={milestones}
            jobId={job?.id || ''}
            onApprovalChange={handleApprovalChange}
            userRole={userRole}
          />
        ) : null}
      </div>

      {/* Completion Confirmation Dialog */}
      <MilestoneCompletionDialog
        open={showCompletionDialog}
        onOpenChange={setShowCompletionDialog}
        milestone={completingMilestone}
        onConfirm={handleConfirmCompletion}
        loading={markCompleteLoading}
      />

      {/* Milestone Set Selector */}
      <MilestoneSetSelector
        open={showMilestoneSetSelector}
        onOpenChange={setShowMilestoneSetSelector}
        onSelect={handleApplyMilestoneSet}
      />

      {/* Milestone Approval Complete Modal */}
      <MilestoneApprovalCompleteModal
        open={showApprovalCompleteModal}
        onClose={() => setShowApprovalCompleteModal(false)}
        onShowPurchaseAgreement={handleShowPurchaseAgreement}
        userRole={userRole}
      />

      {/* Purchase Agreement Modal */}
      <PurchaseAgreementModal
        open={showPurchaseAgreementModal}
        onClose={() => setShowPurchaseAgreementModal(false)}
        onAccept={handleAcceptAgreement}
        onReviewDetails={handleReviewDetails}
        amount={paymentAmount}
        loading={creatingPO}
        taxPreview={taxPreview}
      />

      {/* Payment Method Selection Modal - Only for clients */}
      {userRole === 'client' && (
        <PaymentMethodSelectionModal
          open={showPaymentMethodModal}
          organizationId={organizationId}
          amount={paymentAmount}
          taxAmountCents={taxPreview?.totalTaxCents || 0}
          currentPaymentMethodId={currentPaymentMethodId}
          jobPublicId={job?.public_id}
          onConfirm={handlePaymentMethodConfirmed}
          onCancel={() => setShowPaymentMethodModal(false)}
          loading={creatingPO}
        />
      )}

      {/* Payment Processing Modal */}
      <PaymentProcessingModal
        open={showPaymentProcessingModal}
        amount={paymentAmount}
        status={paymentStatus}
        errorMessage={paymentError}
        errorDetails={paymentErrorDetails}
        transactionId={paymentTransactionId}
        onClose={() => setShowPaymentProcessingModal(false)}
        onUpdatePaymentMethod={() => {
          setShowPaymentProcessingModal(false);
          setShowPaymentMethodModal(true);
        }}
      />
    </div>
  );
}